import { always, both, compose, either, evolve, filter, identity, into, map, pick, pipe, prop, uniq } from 'ramda'
import { createSelector } from 'redux-bundler'

import { camelize } from 'inflection'
import ms from 'milliseconds'

import createListBundle from '~/src/Lib/createListBundle'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_ARRAY, EMPTY_OBJECT, isValidParam } from '~/src/Lib/Utils'
import { InventoryItem } from '~/src/Store/Schemas'
import { createAppIsReadySelector } from '~/src/Store/utils'

import { getInventoryPermits } from '../utils'

const logger = createLogger('createInventoryBundle')

const truthyOrNil = either(identity, always(null))
const arrayIdOrNil = either(
  // if an array, filter out falsey values and return null if empty
  both(Array.isArray, pipe(filter(Boolean), uniq, either(both(prop('length'), identity), always(null)))),
  // otherwise return truthy values or null
  truthyOrNil,
)

const PARAMS_TRANSFORMS = {
  search: truthyOrNil,
  types: arrayIdOrNil,
  harvests: arrayIdOrNil,
  cultivars: arrayIdOrNil,
  rooms: arrayIdOrNil,
  items: arrayIdOrNil,
  itemCategories: arrayIdOrNil,
  grades: arrayIdOrNil,
}

export const getInventoryList = (list, entities, itemFilter = Boolean) =>
  into(
    [],
    compose(
      map(({ uuid }) => entities[uuid]),
      filter(itemFilter),
    ),
    list.results ?? list ?? EMPTY_ARRAY,
  )

const initialInventoryState = {
  search: '',
  types: ['plant', 'plantBatch'],
  harvests: null,
  cultivars: null,
  rooms: null,
  items: null,
  itemCategories: null,
  hideFinished: true,
  grades: null,
}

export const createInventoryBundle = (
  entityName = 'inventory',
  { initialState: initial, name = `${entityName}List`, ...opts } = EMPTY_OBJECT,
) => {
  const initialState = { ...initialInventoryState, ...initial }
  const camelName = camelize(name ?? `${entityName}List`)
  const { [`react${camelName}Fetch`]: reactFetch, ...listBundle } = createListBundle({
    actions: [],
    entityName,
    name,
    initialState,
    permissions: { any: true, keys: [getInventoryPermits] },
    schema: InventoryItem,
    fetchHandler: ({ listState, apiFetch }) => {
      let params = pick(Object.keys(initialState), listState)

      const query = Object.entries(params).reduce((q, [key, value]) => {
        if (isValidParam(value)) {
          return {
            ...q,
            [key]: value,
          }
        }
        return q
      }, EMPTY_OBJECT)
      return apiFetch('/inventory/', { ...query })
    },
    staleAfter: ms.minutes(15),
    retryAfter: ms.seconds(5),
    ...opts,
  })
  logger.debug('creating inventory bundle for', listBundle.name, 'with initial state', initialState)

  const { [`do${camelName}SetParams`]: setParams } = listBundle

  return {
    ...listBundle,
    [`do${camelName}SetParams`]: pipe(setParams, evolve(PARAMS_TRANSFORMS)),
    [`selectCurrent${camelName}`]: createSelector(`select${camelName}`, 'selectEntities', (list, entities) =>
      (list?.results?.length ?? list?.length) ? getInventoryList(list, entities) : EMPTY_ARRAY,
    ),
    [`select${camelName}Params`]: createSelector(`select${camelName}Raw`, pick(Object.keys(initialState))),
    [`react${camelName}Fetch`]: createAppIsReadySelector({
      dependencies: [
        'selectCurrentMembership',
        `select${camelName}ShouldUpdate`,
        `select${camelName}Raw`,
        'selectIsOnline',
        'selectUrlObject',
        'selectRouteInfo',
      ],
      resultFn: (currentMembership, shouldUpdate, listRaw, isOnline, urlObject, routeInfo) => {
        const fetchAction = reactFetch.resultFn(currentMembership, true, urlObject, routeInfo)
        const canFetch = Boolean(fetchAction)
        let urlMatch = true
        if ('urlTest' in opts) {
          const url = urlObject.href.replace(urlObject.origin, '')
          urlMatch = opts.urlTest(url, routeInfo ? (routeInfo.pattern ?? '') : '')
        }
        const { failedPermanently, isLoading } = listRaw
        if (failedPermanently || !isOnline || !currentMembership || !canFetch || !urlMatch || isLoading) return null

        if (shouldUpdate) {
          return fetchAction
        }

        return null
      },
    }),
  }
}
